import { useState } from "react";
import axios from "axios";
import { Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResendButton = ({ id, adminKey, onDone }) => {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/contact/${id}/resend`, {}, { headers: { "X-Admin-Key": adminKey } });
      if (data?.reply_status !== "failed" && data?.alert_status !== "failed") toast.success("E-mails sent.");
      else toast.error("Still failing — check your Emergent balance and try again.");
      await onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={resend} disabled={busy} data-testid="admin-message-resend-button" className="btn-primary !px-4 !py-1.5 text-xs disabled:opacity-50">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />} Send again
    </button>
  );
};

const STATUS = {
  sent: "text-[#5cf2a0] border-[#5cf2a0]/30",
  failed: "text-rose-400 border-rose-400/30",
  skipped: "text-amber-300 border-amber-300/30",
  pending: "text-white/50 border-white/15",
};

const Chip = ({ label, value, testid }) => (
  <span data-testid={testid} className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS[value] || STATUS.pending}`}>
    {label}: {value || "pending"}
  </span>
);

export const AdminMessages = ({ msgs, adminKey, onChange }) => (
  <div data-testid="admin-messages" className="space-y-4">
    {msgs.length === 0 && <p className="text-white/50">No messages yet.</p>}
    {msgs.map((m) => (
      <div key={m.id} data-testid="admin-message-item" className="glass rounded-3xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-display text-lg font-semibold">
            {m.name} <a href={`mailto:${m.email}`} className="ml-2 text-sm font-normal text-[#22d3ee]">{m.email}</a>
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">{m.app} · {m.lang} · {new Date(m.created_at).toLocaleString()}</p>
        </div>
        <p className="mt-4 whitespace-pre-wrap text-white/75">{m.message}</p>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Chip label="AI reply" value={m.reply_status} testid="admin-message-reply-status" />
          <Chip label="Alert" value={m.alert_status} testid="admin-message-alert-status" />
          {(m.reply_status === "failed" || m.alert_status === "failed") && <ResendButton id={m.id} adminKey={adminKey} onDone={onChange} />}
        </div>
        {m.ai_reply && (
          <details className="mt-4 rounded-2xl border border-white/10 p-4">
            <summary data-testid="admin-message-ai-reply-toggle" className="text-sm text-white/60">{m.reply_status === "sent" ? "Show AI reply sent" : "Show AI reply (not delivered)"}</summary>
            <p className="mt-3 whitespace-pre-wrap text-sm text-white/70">{m.ai_reply}</p>
          </details>
        )}
      </div>
    ))}
  </div>
);
